import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { Button } from '@this/shared/ui/inputs/button';
import { Link } from '@this/shared/ui/navigations/link';
import { useAITTheme } from '@this/shared/ui/theme';
import { Fetcher } from '@this/src/util';
import ManualModal from '@this/components/admin/manuals/manual_modal';
import { getStyles } from './manuals.styles';

interface ManualJson {
  id: number;
  name: string;
  file_url: string;
}

interface Response {
  manuals: ManualJson[];
}

const Manuals = observer(() => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [manuals, setManuals] = useState<ManualJson[]>([]);
  const [manual, setManual] = useState<ManualJson | null>(null);
  const [showManualModal, setShowManualModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>('');

  const fetchManuals = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await Fetcher.get<Response>('/admin/manuals.json');
      setManuals(response.manuals);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchManuals();
  }, [fetchManuals]);

  const openModal = useCallback((type: string, selected: ManualJson | null) => {
    setManual(selected);
    setModalType(type);
    setShowManualModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowManualModal(false);
  }, []);

  return (
    <div style={styles.pageLayout}>
      <div style={styles.pageHeader}>
        <h2 style={styles.pageTitle}>マニュアル</h2>
        <Button
          type="button"
          color="primary"
          style={styles.addButton}
          disabled={isLoading}
          onClick={() => openModal('add', null)}
        >
          マニュアルを追加
        </Button>
      </div>
      <div style={styles.contentIn}>
        {isLoading ? (
          <Loading />
        ) : (
          <div className="e2e-manuals-list" style={styles.listCard}>
            {manuals.length === 0 ? (
              <div style={styles.emptyMessage}>マニュアルが登録されていません</div>
            ) : (
              manuals.map((data, index) => (
                <div
                  key={data.id}
                  data-e2e-id={data.id}
                  style={{
                    ...styles.listRow,
                    ...(index === manuals.length - 1 ? styles.listRowLast : {})
                  }}
                >
                  <div style={styles.pdfIcon} aria-hidden>
                    PDF
                  </div>
                  <div style={styles.rowMain}>
                    <div className="e2e-name" style={styles.rowName}>
                      {data.name}
                    </div>
                  </div>
                  <div style={styles.rowActions}>
                    <Link target="_blank" href={data.file_url} isExternal style={styles.viewLink}>
                      表示
                      <OpenInNewIcon style={styles.viewLinkIcon} />
                    </Link>
                    <Button
                      type="button"
                      color="primary"
                      size="small"
                      style={styles.tableActionButton}
                      disabled={isLoading}
                      onClick={() => openModal('mod', data)}
                    >
                      上書き
                    </Button>
                    <Button
                      type="button"
                      color="danger"
                      size="small"
                      style={styles.tableActionButton}
                      disabled={isLoading}
                      onClick={() => openModal('delete', data)}
                    >
                      削除
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {showManualModal && (
        <ManualModal
          open
          onClose={closeModal}
          afterSubmit={() => fetchManuals()}
          id={manual?.id}
          fileName={manual?.name}
          modalType={modalType}
        />
      )}
    </div>
  );
});

export default Manuals;
